import { useHarnessCapabilities } from "@/api/harnessCapabilities";
import { defaultModelFor } from "@/constants/harnesses";
import {
  HARNESS_IDS,
  HarnessCapability,
  HarnessId,
} from "@cohesive/api-client";
import { useLocalStorageState } from "ahooks";
import { useMemo } from "react";

export interface HarnessDraft {
  harness: HarnessId;
  /** Undefined on cohesive, which takes no model, and while the capabilities are still loading. */
  model?: string;
}

const DEFAULT_DRAFT: HarnessDraft = { harness: HARNESS_IDS.COHESIVE };

/**
 * Coerce a stored draft back onto what the user can actually start a chat on today. A harness
 * that has since lost its credential — or that the harness-runtime flag no longer offers — is
 * no longer selectable, and leaving it stored would send a create the server refuses.
 *
 * Unknown capabilities — still loading, or the request failed — fail closed to cohesive, the
 * one harness that needs no permission, because a create can fire before the answer lands and
 * an unverified harness is the one the server 403s. Read-only: the stored choice is untouched,
 * so it comes back intact the moment the catalog does. Nothing renders during that window
 * either — the selector waits on the same answer — so there is no cohesive to flash.
 */
export function sanitizeHarnessDraft(
  draft: HarnessDraft,
  capabilities: HarnessCapability[] | undefined,
): HarnessDraft {
  if (!capabilities) return DEFAULT_DRAFT;

  const capability = capabilities.find((c) => c.id === draft.harness);
  if (!capability?.available) return DEFAULT_DRAFT;
  if (!capability.models.length) return { harness: draft.harness };

  const model = capability.models.some((m) => m.id === draft.model)
    ? draft.model
    : defaultModelFor(capability);

  return { harness: draft.harness, model };
}

/**
 * Persists a session-less composer's harness + model choice under `storageKey` and coerces it
 * back to Cohesive when what it names is no longer selectable. Scope-agnostic, like
 * `useReasoningEffort` next door, so each composer surface keeps its own choice.
 *
 * Draft state only: inside a chat the harness is stamped on the flow session and the model is
 * read from it, so neither is a local preference there.
 */
export function useHarnessDraft(storageKey: string) {
  const { data: capabilities } = useHarnessCapabilities();
  // The choice is read by hooks other than the one the pill writes through — the canvas draft
  // has six live instances, and the new-chat buttons that spend it are never the composer that
  // set it. Without this each instance keeps the snapshot it mounted with and mints on a
  // harness the user has since changed, which the create then stamps for good.
  const [draft, setDraft] = useLocalStorageState<HarnessDraft>(storageKey, {
    defaultValue: DEFAULT_DRAFT,
    listenStorageChange: true,
  });

  // Memoized because the sanitized draft is what the session-create callbacks depend on: a
  // fresh object each render would give every one of them a new identity, and they are props
  // on surfaces that rely on their stability.
  const sanitized = useMemo(
    () => sanitizeHarnessDraft(draft ?? DEFAULT_DRAFT, capabilities),
    [draft, capabilities],
  );

  return [sanitized, setDraft] as const;
}

/**
 * The canvas's standing harness choice, keyed the way `useCanvasReasoningEffort` keys its tier.
 * Every user-started chat on a canvas reads it — the composer that offers the pill and the
 * new-chat buttons that mint a session before the user has typed anything — so a canvas answers
 * consistently however the chat was opened.
 */
export function useCanvasHarnessDraft(canvasId: string | undefined) {
  return useHarnessDraft(`canvas_harness_${canvasId ?? "__none__"}`);
}
